import { ExternalLink, Printer } from 'lucide-react';
import { useZeigerFein } from '@/hooks/useLayout';
import { CopyButton } from '@/components/ui/CopyButton';
import type { Bestellung, KundePatch, LieferadressePatch } from '@/types/detail';
import { KundenQuelleBadge } from './KundenQuelleBadge';
import { useNavigate } from '@tanstack/react-router';
import { anzeigeNummer, zweitNummer } from '@/lib/bestellnummer';
import { KundenkontextBadge } from './KundenkontextBadge';
import { LieferadresseEditor } from './LieferadresseEditor';
import { KundeEditor } from './KundeEditor';
import { UebungBadge } from './UebungBadge';
import { GeschenkBadge } from './GeschenkBadge';
import { UebergabeQr } from './UebergabeQr';
import { KopfDaten } from './KopfDaten';

type Props = {
  bestellung: Bestellung;
  onLieferadresseSave: (patch: LieferadressePatch) => void;
  /** Korrektur der Kundenangaben (A2). */
  onKundeSave: (patch: KundePatch) => void;
};

export function DetailHeader({ bestellung: b, onLieferadresseSave, onKundeSave }: Props) {
  const navigate = useNavigate();
  const zeigerFein = useZeigerFein();
  const la = b.lieferadresse;
  // Shop-Bestellung: gedruckt wird der Glomer-Lieferschein (Nachbau der
  // Order-Printer-Vorlage, live aus Shopify). Er trägt denselben QR-Code
  // wie der Zettel und spart das zweite Blatt. Der Zettel bleibt als
  // Nebenweg — eine Bestellung ohne Lieferschein-Bedarf gibt es auch im
  // Shop (Abholung, Zuordnung zu einer Ladenbestellung).
  const ausShop = b.shopify_order_id !== null;
  // Am PC führt die G-Nummer in den Shopify-Admin. Am Tablet nicht: dort
  // öffnet ein Link den Admin in Safari statt in der App, und der Tipp
  // auf die Nummer ist dort eher ein Versehen als eine Absicht.
  const adminLink = zeigerFein && b.shopify_admin_url ? b.shopify_admin_url : null;
  return (
    <header className="space-y-3 border-b border-line bg-cream px-4 py-4" data-tour="detail-kopf">
      <div className="flex flex-wrap items-start gap-4">
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            {adminLink ? (
              <a
                href={adminLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 font-serif text-xl text-akzent underline-offset-4 hover:underline"
                title="In Shopify öffnen"
              >
                {anzeigeNummer(b)}
                <ExternalLink aria-hidden className="h-4 w-4" />
                <span className="sr-only">(in Shopify öffnen)</span>
              </a>
            ) : (
              <span className="font-serif text-xl text-akzent">{anzeigeNummer(b)}</span>
            )}
            {b.ist_uebung && <UebungBadge />}
            {/* A25: Der Wunsch aus der Aufnahme, gleich neben der
                Nummer — er soll beim Oeffnen ins Auge fallen, nicht
                erst am Packtisch auffindbar sein. */}
            {b.ist_geschenk && <GeschenkBadge />}
            {zweitNummer(b) && (
              <span className="font-serif text-sm text-ink-faint">· {zweitNummer(b)}</span>
            )}
            {/* Neues Fenster: der Druckdialog oeffnet sich dort von selbst,
                die Detailansicht bleibt daneben stehen. */}
            <button
 data-nutzung="zettel_drucken"              type="button"
              onClick={() =>
                void navigate(
                  ausShop
                    ? {
                        to: '/bestellungen/$bestellnr/lieferschein',
                        params: { bestellnr: b.bestellnr },
                        search: {}
                      }
                    : { to: '/bestellungen/$bestellnr/druck', params: { bestellnr: b.bestellnr } }
                )
              }
              data-tour="detail-drucken"
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-md border border-line px-3 text-sm text-ink-soft hover:border-akzent hover:text-akzent"
            >
              <Printer className="h-4 w-4" />
              {ausShop ? 'Lieferschein drucken' : 'Zettel drucken'}
            </button>
            {ausShop && (
              <button
                type="button"
                onClick={() =>
                  void navigate({
                    to: '/bestellungen/$bestellnr/druck',
                    params: { bestellnr: b.bestellnr }
                  })
                }
                className="inline-flex min-h-[44px] items-center px-2 text-xs text-ink-faint underline-offset-2 hover:text-akzent hover:underline"
              >
                Nur Zettel
              </button>
            )}
            <KundenQuelleBadge
              quelle={b.kunde.quelle}
              syncedAt={b.kunde.synced_at}
              ausShopify={b.shopify_order_number !== null}
            />
            {/* A9.5: Die BONUS-Nummer wird beim Arbeiten in BONUS
                getippt — klein und grau war sie dafür das falsche
                Format. Jetzt in Tabellenziffern, mit Kopier-Knopf
                daneben; der bringt sein eigenes 44er-Berührziel mit. */}
            {b.kunde.bonus_kundennr && (
              <span className="inline-flex items-center gap-0.5 rounded border border-line bg-cream-warm py-0.5 pl-2 text-sm tabular-nums text-ink">
                <span className="text-ink-faint">#</span>
                {b.kunde.bonus_kundennr}
                <CopyButton
                  value={b.kunde.bonus_kundennr}
                  label={`BONUS-Nummer ${b.kunde.bonus_kundennr} kopieren`}
                />
              </span>
            )}
          </div>
          <p className="font-serif text-lg text-ink">{b.kunde.anzeigename}</p>
          {(la.strasse || la.ort) && (
            <p className="text-sm text-ink-soft">
              {[la.strasse, [la.plz, la.ort].filter(Boolean).join(' ')]
                .filter(Boolean)
                .join(', ')}
              {la.land && la.land !== 'Deutschland' ? `, ${la.land}` : ''}
            </p>
          )}
          {(b.kunde.telefon || b.kunde.email) && (
            <p className="text-xs text-ink-faint">
              {[b.kunde.telefon, b.kunde.email].filter(Boolean).join(' · ')}
            </p>
          )}
          <KopfDaten b={b} />
          <KundenkontextBadge
            weitereOffene={b.kunden_kontext.weitere_offene}
            bestellungen12Monate={b.kunden_kontext.bestellungen_letzte_12_monate}
          />
          {/* Zwei Editoren, zwei Gegenstände: die Kundin (Stammsatz) und
              die Anschrift dieser einen Lieferung (Kopie am Vorgang).

              DER ABSTAND GEHÖRT HIERHER (A5.4)

              Er stand vorher nirgends. Über dieser Stelle liegt
              `space-y-1`, und das wirkt nur zwischen Block-Elementen —
              beide Editoren geben im geschlossenen Zustand einen
              `inline-flex`-Knopf zurück, also landeten sie auf derselben
              Zeile und berührten sich. Gemessen bei 820 und bei 390
              Pixeln: Abstand jeweils 0.

              Keiner der beiden Editoren kann das für sich lösen — keiner
              weiß, dass der andere daneben steht. Deshalb der eigene
              Rahmen hier, im gemeinsamen Elternteil.

              Schmal untereinander, ab `sm` nebeneinander mit echtem
              Zwischenraum. Auf 390 Pixeln steht damit nichts von beidem
              neben etwas anderem — was der Kommentar an dieser Stelle
              schon immer behauptet hat. */}
          {ausShop ? (
            // Glomer (E47): Kunde und Anschrift führt Shopify. Von dort
            // kommen Lieferschein und easyDPD-Etikett, und der Abgleich
            // frischt die Namen bei jeder Bestellung auf (#171/#173). Eine
            // Änderung hier erreichte Shopify nicht und würde dort
            // überschrieben — zwei Wahrheiten, die auseinanderlaufen.
            <p className="pt-1 text-xs text-ink-faint">
              Kunde und Anschrift ändern:{' '}
              {b.shopify_admin_url ? (
                <a
                  href={b.shopify_admin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex min-h-[44px] items-center text-akzent underline underline-offset-2"
                >
                  in Shopify
                </a>
              ) : (
                'in Shopify'
              )}
            </p>
          ) : (
            <div className="flex flex-col items-start gap-y-2 pt-1 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-6">
              <KundeEditor bestellungId={b.id} kunde={b.kunde} onSave={onKundeSave} />
              <LieferadresseEditor
                bestellungId={b.id}
                adresse={la}
                onSave={onLieferadresseSave}
              />
            </div>
          )}
        </div>
        {/* Rechts neben der Nummer, nicht darunter: der Kopf ist die
            Stelle, an der man die Bestellung identifiziert, und der Code
            ist nichts anderes als die Nummer in anderer Form. */}
        <UebergabeQr bestellnr={b.bestellnr} />
      </div>
    </header>
  );
}
